import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight, Plus, Trash2, Save, FolderOpen, Printer } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  LAND_UNITS,
  UNITS_BY_ID,
  QUICK_CHIPS,
  type LandUnit,
} from "@/data/bdLandUnits";
import {
  convertLand,
  convertToAll,
  formatLandNumber,
  summarizePlots,
  plotAreaSqft,
  fromSqft,
  estimatePrice,
  estimateRegistrationCost,
  type Plot,
  type Shape,
  type LengthUnit,
} from "@/lib/calculators/landCalculator";

type B = { en: string; bn: string };
type Lang = "en" | "bn";

const T = {
  h1: { en: "Bangladesh Land Calculator", bn: "জমির হিসাব ক্যালকুলেটর" },
  desc: {
    en: "Convert katha, bigha, shotok, decimal, kani, gonda, square feet, acre and hectare. Multi-plot summary, plot area calculator and land price estimator — all in one place.",
    bn: "কাঠা, বিঘা, শতক, ডেসিমেল, কানি, গণ্ডা, বর্গফুট, একর, হেক্টর — সব এক জায়গায়। মাল্টি-প্লট, প্লট পরিমাপ ও জমির দাম এস্টিমেটর সহ।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  amount: { en: "Amount", bn: "পরিমাণ" },
  from: { en: "From", bn: "থেকে" },
  to: { en: "To", bn: "তে" },
  swap: { en: "Swap", bn: "অদলবদল" },
  showAll: { en: "Show all conversions", bn: "সব এককে দেখুন" },
  result: { en: "Result", bn: "ফলাফল" },
  print: { en: "Print", bn: "প্রিন্ট" },
  category: {
    standard: { en: "Standard", bn: "প্রচলিত" },
    regional: { en: "Regional (Chittagong / Sylhet)", bn: "আঞ্চলিক (চট্টগ্রাম / সিলেট)" },
    international: { en: "International", bn: "আন্তর্জাতিক" },
    traditional: { en: "Traditional", bn: "ঐতিহ্যবাহী" },
  },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "1 katha is how many square feet?", bn: "১ কাঠা কত স্কয়ার ফিট?" },
    a: { en: "1 Katha (Bangladesh standard) = 720 sq ft. This is the value used by RAJUK and most district land records.",
         bn: "১ কাঠা (বাংলাদেশ মান) = ৭২০ বর্গফুট। RAJUK ও বেশিরভাগ জেলার রেকর্ডে এই মান ব্যবহৃত হয়।" } },
  { q: { en: "1 bigha is how many katha?", bn: "১ বিঘা কত কাঠা?" },
    a: { en: "1 Bigha (BD) = 20 Katha = 14,400 sq ft = 33.058 Decimal.",
         bn: "১ বিঘা = ২০ কাঠা = ১৪,৪০০ বর্গফুট = ৩৩.০৫৮ ডেসিমেল।" } },
  { q: { en: "1 acre is how many bigha?", bn: "১ একর সমান কত বিঘা?" },
    a: { en: "1 Acre = 100 Decimal = 43,560 sq ft ≈ 3.025 Bigha (BD standard).",
         bn: "১ একর = ১০০ ডেসিমেল = ৪৩,৫৬০ বর্গফুট ≈ ৩.০২৫ বিঘা।" } },
  { q: { en: "Difference between Shotok and Decimal?", bn: "শতক ও ডেসিমেল কি একই?" },
    a: { en: "In Bangladesh they are identical — both equal 1/100 of an Acre = 435.6 sq ft. ‘Shotangsho/Shotok’ is the local Bengali term; ‘Decimal’ is the colonial-era term used on the same records.",
         bn: "বাংলাদেশে এ দুটো একই — ১/১০০ একর = ৪৩৫.৬ বর্গফুট। শতাংশ/শতক স্থানীয় বাংলা পরিভাষা; ডেসিমেল ঔপনিবেশিক যুগের একই এককের ইংরেজি নাম।" } },
  { q: { en: "How much is 1 hectare in bigha?", bn: "১ হেক্টর সমান কত বিঘা?" },
    a: { en: "1 Hectare = 10,000 sq m = 107,639 sq ft ≈ 7.474 Bigha = 247.1 Decimal = 2.471 Acre.",
         bn: "১ হেক্টর = ১০,০০০ বর্গমিটার = ১০৭,৬৩৯ বর্গফুট ≈ ৭.৪৭৪ বিঘা = ২৪৭.১ ডেসিমেল।" } },
  { q: { en: "What is Kani in Chittagong?", bn: "চট্টগ্রামের কানি কী?" },
    a: { en: "Chittagong / Noakhali use Kani as a key unit. 1 Kani = 40 Decimal = 17,424 sq ft. 1 Kani = 20 Gonda; 1 Gonda = 2 Decimal.",
         bn: "চট্টগ্রাম-নোয়াখালীতে কানি প্রধান একক। ১ কানি = ৪০ ডেসিমেল = ১৭,৪২৪ বর্গফুট = ২০ গণ্ডা; ১ গণ্ডা = ২ ডেসিমেল।" } },
  { q: { en: "Is the Sylhet bigha different from the Dhaka bigha?", bn: "সিলেটের বিঘা ঢাকার বিঘা থেকে আলাদা?" },
    a: { en: "Some Sylhet and North-Bengal mouzas historically record bigha at ~12,470 sq ft instead of the standard 14,400 sq ft. Always verify the bigha definition against the local Khatian / Mouza map before any transaction.",
         bn: "সিলেট ও উত্তরবঙ্গের কিছু মৌজায় ঐতিহাসিকভাবে ১ বিঘা ≈ ১২,৪৭০ বর্গফুট ছিল, যা প্রচলিত ১৪,৪০০-এর চেয়ে আলাদা। লেনদেনের আগে স্থানীয় খতিয়ান যাচাই করুন।" } },
  { q: { en: "How do I read a Khatian / Porcha?", bn: "জমির খতিয়ান কীভাবে পড়ব?" },
    a: { en: "A Khatian lists owner names, plot (dag) numbers, mouza, and area — usually in Decimal/Shotangsho. Cross-check the dag number on the Mouza map and confirm current possession at the local AC Land office before relying on it.",
         bn: "খতিয়ানে মালিকের নাম, দাগ নম্বর, মৌজা ও জমির পরিমাণ (সাধারণত শতাংশে) থাকে। মৌজা ম্যাপের সাথে দাগ মিলিয়ে এসি ল্যান্ড অফিসে বর্তমান দখল যাচাই করুন।" } },
  { q: { en: "Approximate land registration cost in Bangladesh?", bn: "জমি রেজিস্ট্রেশন খরচ কত?" },
    a: { en: "Roughly 12–15% of deed value: gain tax ~4%, stamp duty ~1.5%, VAT ~3%, registration fee ~1%, local government tax ~3% in city corporations. Use the registration cost panel for a rough estimate — actual rates depend on land class and location.",
         bn: "মোটামুটি দলিল মূল্যের ১২–১৫% — গেইন ট্যাক্স ~৪%, স্ট্যাম্প ডিউটি ~১.৫%, ভ্যাট ~৩%, রেজিস্ট্রেশন ফি ~১%, সিটি কর্পোরেশনে স্থানীয় সরকার কর ~৩%। সঠিক হার জমির শ্রেণি ও এলাকা অনুসারে।" } },
  { q: { en: "What is 1 Gonda / Kora / Kranti / Til?", bn: "১ গণ্ডা / কড়া / ক্রান্তি / তিল কী?" },
    a: { en: "Traditional sub-units of the Decimal/Kani system: 1 Gonda = 2 Decimal, 1 Kora = 0.5 Decimal, 1 Kranti = 1/8 Decimal, 1 Til = 1/32 Decimal. Mostly used in older Chittagong & Noakhali deeds.",
         bn: "ডেসিমেল/কানি পদ্ধতির প্রচলিত উপ-একক: ১ গণ্ডা = ২ ডেসিমেল, ১ কড়া = ০.৫ ডেসিমেল, ১ ক্রান্তি = ১/৮ ডেসিমেল, ১ তিল = ১/৩২ ডেসিমেল।" } },
  { q: { en: "How is 1 Hath (cubit) related to area?", bn: "১ হাত কত বর্গফুট?" },
    a: { en: "1 Hath ≈ 1.5 ft (linear). When measuring small areas in hath × hath, 1 sq Hath = 2.25 sq ft.",
         bn: "১ হাত ≈ ১.৫ ফুট। ১ বর্গহাত = ২.২৫ বর্গফুট।" } },
  { q: { en: "How accurate is the price estimator?", bn: "জমির দাম এস্টিমেটর কতটা সঠিক?" },
    a: { en: "It does straight-line math — your area × price-per-unit, plus a typical Bangladesh registration cost split (~12–15%). Real prices depend on location, road access, frontage, and current market — always verify locally.",
         bn: "এটি সরল হিসাব — পরিমাণ × প্রতি এককের দাম, সাথে প্রায় ১২–১৫% রেজিস্ট্রেশন খরচ। প্রকৃত দাম এলাকা, রাস্তা ও বাজারের ওপর নির্ভর — স্থানীয়ভাবে যাচাই করুন।" } },
  { q: { en: "How do I compute an irregular plot's area?", bn: "অনিয়মিত প্লটের আয়তন কীভাবে?" },
    a: { en: "Split the quadrilateral with one diagonal into two triangles, measure all four sides plus the diagonal, then use Heron's formula on each triangle. The Plot Area tab does this automatically.",
         bn: "চার-কোনা প্লটটি একটি কর্ণ দ্বারা দুই ত্রিভুজে ভাগ করুন, চার বাহু ও কর্ণ মাপুন; তারপর হেরন সূত্র প্রয়োগ করুন। 'প্লট পরিমাপ' ট্যাব এটা স্বয়ংক্রিয়ভাবে করে।" } },
  { q: { en: "5 katha is how many square feet?", bn: "৫ কাঠা কত বর্গফুট?" },
    a: { en: "5 × 720 = 3,600 sq ft = 8.265 Decimal = 0.25 Bigha.",
         bn: "৫ × ৭২০ = ৩,৬০০ বর্গফুট = ৮.২৬৫ ডেসিমেল = ০.২৫ বিঘা।" } },
  { q: { en: "Does the calculator save my plots?", bn: "ক্যালকুলেটর কি প্লট সেভ করে রাখে?" },
    a: { en: "Yes — multi-plot entries auto-save in your browser (localStorage). Use ‘Save’ to bookmark a snapshot you can re-open later on the same device.",
         bn: "হ্যাঁ — মাল্টি-প্লট তালিকা ব্রাউজারে স্বয়ংক্রিয়ভাবে সেভ হয়। 'সেভ' বোতামে স্ন্যাপশট রাখুন, পরে একই ডিভাইসে খুলতে পারবেন।" } },
];

export const Route = createFileRoute("/land-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Land Calculator BD — Katha, Bigha, Shotok, Decimal",
      description:
        "Free Bangladesh land calculator: convert katha, bigha, shotok, decimal, kani, gonda, sq ft, acre, hectare. জমির হিসাব ক্যালকুলেটর।",
      path: "/land-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Land Calculator", path: "/land-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: LandCalculatorPage,
});

const STORAGE_KEY = "bcalc.land.plots.v1";

function LandCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number) => num(formatLandNumber(n));

  // ===== Section 1: Converter =====
  const [amountStr, setAmountStr] = useState("1");
  const [fromId, setFromId] = useState("bigha");
  const [toId, setToId] = useState("sqft");
  const [showAll, setShowAll] = useState(false);

  const amount = parseFloat(amountStr) || 0;
  const converted = useMemo(() => convertLand(amount, fromId, toId), [amount, fromId, toId]);
  const allConversions = useMemo(() => convertToAll(amount, fromId), [amount, fromId]);

  const swap = () => {
    setFromId(toId);
    setToId(fromId);
  };

  const fromUnit = UNITS_BY_ID[fromId];
  const toUnit = UNITS_BY_ID[toId];

  // ===== Section 2: Multi-plot =====
  const [plots, setPlots] = useState<Plot[]>([
    { id: crypto.randomUUID(), amount: 5, unitId: "katha", label: "Plot 1" },
    { id: crypto.randomUUID(), amount: 12, unitId: "decimal", label: "Plot 2" },
  ]);
  const [totalUnitId, setTotalUnitId] = useState("sqft");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setPlots(parsed);
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(plots)); } catch { /* ignore */ }
  }, [plots]);

  const summary = useMemo(() => summarizePlots(plots), [plots]);

  const addPlot = () =>
    setPlots((ps) => [...ps, { id: crypto.randomUUID(), amount: 1, unitId: "katha", label: `Plot ${ps.length + 1}` }]);
  const removePlot = (id: string) => setPlots((ps) => ps.filter((p) => p.id !== id));
  const updatePlot = (id: string, patch: Partial<Plot>) =>
    setPlots((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  // ===== Section 3: Price estimator =====
  const [priceArea, setPriceArea] = useState("5");
  const [priceAreaUnit, setPriceAreaUnit] = useState("katha");
  const [pricePer, setPricePer] = useState("5000000");
  const [priceUnit, setPriceUnit] = useState("katha");
  const [showRegCost, setShowRegCost] = useState(false);

  const price = useMemo(
    () =>
      estimatePrice({
        area: parseFloat(priceArea) || 0,
        areaUnitId: priceAreaUnit,
        pricePerUnit: parseFloat(pricePer) || 0,
        priceUnitId: priceUnit,
      }),
    [priceArea, priceAreaUnit, pricePer, priceUnit],
  );
  const regCost = useMemo(() => estimateRegistrationCost({ totalPrice: price.totalPrice }), [price.totalPrice]);

  // ===== Section 4: Plot area =====
  const [shape, setShape] = useState<Shape>("rectangle");
  const [lenUnit, setLenUnit] = useState<LengthUnit>("ft");
  const [dims, setDims] = useState<Record<string, string>>({
    length: "60", width: "80",
    base: "50", height: "40",
    side1: "30", side2: "40", side3: "50",
    parallelA: "30", parallelB: "50",
    s1: "30", s2: "40", s3: "30", s4: "40", diagonal: "50",
  });
  const [areaOutUnit, setAreaOutUnit] = useState("sqft");

  const setDim = (k: string, v: string) => setDims((d) => ({ ...d, [k]: v }));
  const numericDims = useMemo(
    () => Object.fromEntries(Object.entries(dims).map(([k, v]) => [k, parseFloat(v) || 0])),
    [dims],
  );
  const areaSqft = useMemo(
    () => plotAreaSqft({ shape, unit: lenUnit, ...numericDims }),
    [shape, lenUnit, numericDims],
  );
  const areaInOut = useMemo(() => fromSqft(areaSqft, areaOutUnit), [areaSqft, areaOutUnit]);

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <CalculatorLayout
      slug="land-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Map"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
        >
          <Printer size={12} /> {tr("print")}
        </button>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
      </div>

      {/* ============ Section 1: Converter ============ */}
      <section className="space-y-4">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Unit converter" : "একক রূপান্তরকারী"}
        </h2>

        <div className="grid items-end gap-3 md:grid-cols-[1.6fr_auto_1fr_auto_1fr]">
          {/* Amount + From */}
          <div className="space-y-1.5">
            <label className={cn("block text-xs font-medium text-muted-foreground md:text-sm", lang === "bn" && "font-bn")}>
              {tr("amount")}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={useBn ? toBengaliNumerals(amountStr) : amountStr}
              onChange={(e) => {
                const v = e.target.value.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
                setAmountStr(v);
              }}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-base font-semibold tabular-nums focus:border-accent focus:outline-none md:px-4 md:py-3.5 md:text-2xl"
            />
          </div>
          <div className="space-y-1.5">
            <label className={cn("block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("from")}
            </label>
            <UnitSelect value={fromId} onChange={setFromId} lang={lang} />
          </div>

          {/* Swap */}
          <div className="flex items-center justify-center pb-1">
            <button
              type="button"
              onClick={swap}
              aria-label={tr("swap")}
              className="rounded-full border border-border bg-card p-2 text-muted-foreground hover:border-accent hover:text-accent"
            >
              <ArrowLeftRight size={16} />
            </button>
          </div>

          {/* To */}
          <div className="space-y-1.5">
            <label className={cn("block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("to")}
            </label>
            <UnitSelect value={toId} onChange={setToId} lang={lang} />
          </div>

          <div className="hidden md:block" />
        </div>

        {/* Result */}
        <motion.div
          key={`${amountStr}-${fromId}-${toId}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-accent/40 bg-accent/5 p-5"
        >
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("result")}
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums md:text-3xl">
            {fmt(amount)}{" "}
            <span className="text-base font-normal text-muted-foreground">
              {lang === "en" ? fromUnit?.en : fromUnit?.bn}
            </span>{" "}
            ={" "}
            <span className="text-accent">{fmt(converted)}</span>{" "}
            <span className="text-base font-normal text-muted-foreground">
              {lang === "en" ? toUnit?.en : toUnit?.bn}
            </span>
          </p>
          <p className="mt-2 font-bn text-sm text-muted-foreground">
            {toBengaliNumerals(formatLandNumber(amount))} {fromUnit?.bn} ={" "}
            {toBengaliNumerals(formatLandNumber(converted))} {toUnit?.bn}
          </p>
        </motion.div>

        {/* Quick chips */}
        <div className="flex flex-wrap gap-2 print:hidden">
          {QUICK_CHIPS.map((c) => {
            const f = UNITS_BY_ID[c.from];
            const t = UNITS_BY_ID[c.to];
            return (
              <button
                key={`${c.from}-${c.to}`}
                type="button"
                onClick={() => { setAmountStr(String(c.amount)); setFromId(c.from); setToId(c.to); }}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-accent"
              >
                {num(c.amount)} {lang === "en" ? f.en : f.bn} → {lang === "en" ? t.en : t.bn}
              </button>
            );
          })}
        </div>

        <label className="inline-flex items-center gap-2 text-sm print:hidden">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          <span className={cn(lang === "bn" && "font-bn")}>{tr("showAll")}</span>
        </label>

        {showAll && (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                  <th className="p-2 text-left font-bn">{lang === "en" ? "Bengali" : "বাংলা"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Value" : "মান"}</th>
                  <th className="p-2 text-left">{lang === "en" ? "Category" : "শ্রেণি"}</th>
                </tr>
              </thead>
              <tbody>
                {allConversions.map(({ unit, value }) => (
                  <tr key={unit.id} className="border-t border-border">
                    <td className="p-2 font-medium">{unit.en}{unit.symbol ? ` (${unit.symbol})` : ""}</td>
                    <td className="p-2 font-bn text-muted-foreground">{unit.bn}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(value)}</td>
                    <td className="p-2 text-xs text-muted-foreground">{T.category[unit.category][lang]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============ Section 2: Multi-plot ============ */}
      <section className="mt-12 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Multi-plot summary" : "মাল্টি-প্লট সারসংক্ষেপ"}
          </h2>
          <button
            type="button"
            onClick={addPlot}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
          >
            <Plus size={12} /> {lang === "en" ? "Add plot" : "প্লট যোগ"}
          </button>
        </div>

        <div className="space-y-2">
          {plots.map((p, i) => (
            <div key={p.id} className="grid grid-cols-12 items-center gap-2 rounded-xl border border-border bg-card p-2">
              <input
                type="text"
                value={p.label ?? `Plot ${i + 1}`}
                onChange={(e) => updatePlot(p.id, { label: e.target.value })}
                className="col-span-3 rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
              <input
                type="text"
                inputMode="decimal"
                value={useBn ? toBengaliNumerals(p.amount) : String(p.amount)}
                onChange={(e) => {
                  const v = e.target.value.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
                  updatePlot(p.id, { amount: parseFloat(v) || 0 });
                }}
                className="col-span-3 rounded-md border border-border bg-background px-2 py-1.5 text-sm tabular-nums focus:border-accent focus:outline-none"
              />
              <div className="col-span-5">
                <UnitSelect value={p.unitId} onChange={(v) => updatePlot(p.id, { unitId: v })} lang={lang} compact />
              </div>
              <button
                type="button"
                onClick={() => removePlot(p.id)}
                className="col-span-1 inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-destructive"
                aria-label="Remove plot"
              >
                <Trash2 size={14} />
              </button>
              <p className="col-span-12 px-1 text-xs text-muted-foreground">
                = {fmt(summary.plots[i]?.sqft ?? 0)} {lang === "en" ? "sq ft" : "বর্গফুট"}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Total" : "মোট"}
            </p>
            <div className="flex items-center gap-2">
              <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {lang === "en" ? "show in" : "দেখান"}
              </span>
              <UnitSelect value={totalUnitId} onChange={setTotalUnitId} lang={lang} compact />
            </div>
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums md:text-3xl">
            {fmt(summary.totalIn(totalUnitId))}{" "}
            <span className="text-base font-normal text-muted-foreground">
              {lang === "en" ? UNITS_BY_ID[totalUnitId]?.en : UNITS_BY_ID[totalUnitId]?.bn}
            </span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            = {fmt(summary.totalSqft)} sq ft = {fmt(fromSqft(summary.totalSqft, "katha"))} katha ={" "}
            {fmt(fromSqft(summary.totalSqft, "decimal"))} decimal
          </p>
        </div>
      </section>

      {/* ============ Section 3: Price estimator ============ */}
      <section className="mt-12 space-y-4">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Land price estimator" : "জমির দাম এস্টিমেটর"}
        </h2>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1.5">
            <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Area" : "পরিমাণ"}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={useBn ? toBengaliNumerals(priceArea) : priceArea}
              onChange={(e) => setPriceArea(e.target.value.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString()))}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm tabular-nums focus:border-accent focus:outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Area unit" : "একক"}
            </label>
            <UnitSelect value={priceAreaUnit} onChange={setPriceAreaUnit} lang={lang} compact />
          </div>
          <div className="space-y-1.5">
            <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Price per unit (৳)" : "প্রতি এককের দাম (৳)"}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={useBn ? toBengaliNumerals(pricePer) : pricePer}
              onChange={(e) => setPricePer(e.target.value.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString()))}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm tabular-nums focus:border-accent focus:outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Per" : "প্রতি"}
            </label>
            <UnitSelect value={priceUnit} onChange={setPriceUnit} lang={lang} compact />
          </div>
        </div>

        <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Estimated total price" : "আনুমানিক মোট দাম"}
          </p>
          <p className="mt-1 text-3xl font-bold md:text-4xl">
            {formatBDT(price.totalPrice, useBn ? "bn" : "en", 0)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            ≈ {formatBDT(price.pricePerSqft, useBn ? "bn" : "en", 0)} / {lang === "en" ? "sq ft" : "বর্গফুট"}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowRegCost((v) => !v)}
          className="text-xs text-accent hover:underline print:hidden"
        >
          {showRegCost
            ? (lang === "en" ? "Hide registration cost estimate" : "রেজিস্ট্রেশন খরচ লুকান")
            : (lang === "en" ? "Show registration cost estimate" : "রেজিস্ট্রেশন খরচ দেখান")}
        </button>

        {showRegCost && (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Component" : "মদ"}</th>
                  <th className="p-2 text-right">%</th>
                  <th className="p-2 text-right">{lang === "en" ? "Amount" : "পরিমাণ"}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: { en: "Gain Tax", bn: "গেইন ট্যাক্স" }, pct: 4, val: regCost.gainTax },
                  { label: { en: "Stamp Duty", bn: "স্ট্যাম্প ডিউটি" }, pct: 1.5, val: regCost.stampDuty },
                  { label: { en: "VAT", bn: "ভ্যাট" }, pct: 3, val: regCost.vat },
                  { label: { en: "Registration Fee", bn: "রেজিস্ট্রেশন ফি" }, pct: 1, val: regCost.regFee },
                  { label: { en: "Local Govt. Tax", bn: "স্থানীয় সরকার কর" }, pct: 3, val: regCost.localGovt },
                ].map((r) => (
                  <tr key={r.label.en} className="border-t border-border">
                    <td className={cn("p-2", lang === "bn" && "font-bn")}>{r.label[lang]}</td>
                    <td className="p-2 text-right tabular-nums">{num(r.pct)}%</td>
                    <td className="p-2 text-right tabular-nums">{formatBDT(r.val, useBn ? "bn" : "en", 0)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-accent/30 bg-accent/5 font-semibold">
                  <td className={cn("p-2", lang === "bn" && "font-bn")}>{lang === "en" ? "Total reg. cost" : "মোট রেজিস্ট্রেশন খরচ"}</td>
                  <td className="p-2 text-right tabular-nums">{num(regCost.pctOfPrice.toFixed(1))}%</td>
                  <td className="p-2 text-right tabular-nums">{formatBDT(regCost.total, useBn ? "bn" : "en", 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============ Section 4: Plot area ============ */}
      <section className="mt-12 space-y-4">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Plot area calculator" : "প্লট পরিমাপ ক্যালকুলেটর"}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          {(["rectangle", "triangle", "trapezium", "irregular"] as Shape[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setShape(s)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium",
                shape === s
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-border bg-card hover:border-accent/40",
                lang === "bn" && "font-bn",
              )}
            >
              {{
                rectangle: { en: "Rectangle", bn: "আয়তক্ষেত্র" },
                triangle: { en: "Triangle", bn: "ত্রিভুজ" },
                trapezium: { en: "Trapezium", bn: "ট্রাপিজিয়াম" },
                irregular: { en: "Irregular (4-sided)", bn: "অনিয়মিত (৪ বাহু)" },
              }[s][lang]}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Length unit:" : "দৈর্ঘ্যের একক:"}
            </span>
            {(["ft", "m", "hath"] as LengthUnit[]).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setLenUnit(u)}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs",
                  lenUnit === u ? "border-accent bg-accent/10 text-accent" : "border-border bg-card",
                )}
              >
                {u === "hath" ? (lang === "en" ? "hath" : "হাত") : u}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {shape === "rectangle" && (
            <>
              <DimField label={lang === "en" ? "Length" : "দৈর্ঘ্য"} v={dims.length} on={(v) => setDim("length", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Width" : "প্রস্থ"} v={dims.width} on={(v) => setDim("width", v)} useBn={useBn} />
            </>
          )}
          {shape === "triangle" && (
            <>
              <DimField label={lang === "en" ? "Base" : "ভূমি"} v={dims.base} on={(v) => setDim("base", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Height" : "উচ্চতা"} v={dims.height} on={(v) => setDim("height", v)} useBn={useBn} />
              <p className="md:col-span-3 text-xs text-muted-foreground">
                {lang === "en" ? "Or, leave blank and enter all 3 sides (Heron's formula):" : "অথবা ৩ বাহু দিন (হেরন সূত্র):"}
              </p>
              <DimField label={lang === "en" ? "Side 1" : "বাহু ১"} v={dims.side1} on={(v) => setDim("side1", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Side 2" : "বাহু ২"} v={dims.side2} on={(v) => setDim("side2", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Side 3" : "বাহু ৩"} v={dims.side3} on={(v) => setDim("side3", v)} useBn={useBn} />
            </>
          )}
          {shape === "trapezium" && (
            <>
              <DimField label={lang === "en" ? "Parallel side a" : "সমান্তরাল a"} v={dims.parallelA} on={(v) => setDim("parallelA", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Parallel side b" : "সমান্তরাল b"} v={dims.parallelB} on={(v) => setDim("parallelB", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Height" : "উচ্চতা"} v={dims.height} on={(v) => setDim("height", v)} useBn={useBn} />
            </>
          )}
          {shape === "irregular" && (
            <>
              <DimField label={lang === "en" ? "Side 1" : "বাহু ১"} v={dims.s1} on={(v) => setDim("s1", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Side 2" : "বাহু ২"} v={dims.s2} on={(v) => setDim("s2", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Side 3" : "বাহু ৩"} v={dims.s3} on={(v) => setDim("s3", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Side 4" : "বাহু ৪"} v={dims.s4} on={(v) => setDim("s4", v)} useBn={useBn} />
              <DimField label={lang === "en" ? "Diagonal (corner 1↔3)" : "কর্ণ (১↔৩)"} v={dims.diagonal} on={(v) => setDim("diagonal", v)} useBn={useBn} />
            </>
          )}
        </div>

        <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Plot area" : "প্লটের আয়তন"}
            </p>
            <UnitSelect value={areaOutUnit} onChange={setAreaOutUnit} lang={lang} compact />
          </div>
          <p className="mt-1 text-2xl font-bold tabular-nums md:text-3xl">
            {fmt(areaInOut)}{" "}
            <span className="text-base font-normal text-muted-foreground">
              {lang === "en" ? UNITS_BY_ID[areaOutUnit]?.en : UNITS_BY_ID[areaOutUnit]?.bn}
            </span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            = {fmt(areaSqft)} sq ft = {fmt(fromSqft(areaSqft, "katha"))} katha ={" "}
            {fmt(fromSqft(areaSqft, "decimal"))} decimal
          </p>
        </div>
      </section>

      {/* ============ Disclaimers ============ */}
      <section className="mt-10 space-y-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-xs">
        <p className={cn("font-semibold text-amber-600 dark:text-amber-400", lang === "bn" && "font-bn")}>
          {lang === "en" ? "⚠ Important notes" : "⚠ গুরুত্বপূর্ণ তথ্য"}
        </p>
        <ul className={cn("ml-5 list-disc space-y-1 text-muted-foreground", lang === "bn" && "font-bn")}>
          <li>
            {lang === "en"
              ? "Bigha varies by region. Bangladesh standard = 14,400 sq ft. Sylhet and some other regions may use different values. Always verify with local mouza records."
              : "বিঘা অঞ্চলভেদে ভিন্ন হয়। বাংলাদেশের মান = ১৪,৪০০ বর্গফুট। সিলেট ও কিছু অঞ্চল ভিন্ন মান ব্যবহার করতে পারে। স্থানীয় মৌজা রেকর্ড যাচাই করুন।"}
          </li>
          <li>
            {lang === "en"
              ? "Land area on documents may differ from physical measurement due to historical surveys. For legal purposes, refer to Khatian, Mouza map, and current AC Land office records."
              : "ঐতিহাসিক জরিপের কারণে দলিলের জমির পরিমাণ মাঠ-পরিমাপ থেকে আলাদা হতে পারে। আইনি কাজে খতিয়ান, মৌজা ম্যাপ ও এসি ল্যান্ড অফিসের রেকর্ড দেখুন।"}
          </li>
          <li>
            {lang === "en"
              ? "Land price estimator is for general reference only — actual market prices vary by location, road access, and market conditions."
              : "জমির দাম এস্টিমেটর শুধু সাধারণ রেফারেন্সের জন্য — প্রকৃত বাজারমূল্য এলাকা, রাস্তা ও বাজারের ওপর নির্ভর করে।"}
          </li>
        </ul>
      </section>

      {/* ============ Educational content ============ */}
      <article className="mt-12 space-y-10 print:hidden">
        <Section title={lang === "en" ? "Bangladesh land units explained" : "বাংলাদেশের জমির একক"} lang={lang}>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                  <th className="p-2 text-left font-bn">বাংলা</th>
                  <th className="p-2 text-right">Sq ft</th>
                  <th className="p-2 text-right">Sq m</th>
                  <th className="p-2 text-right">Decimal</th>
                  <th className="p-2 text-left">{lang === "en" ? "Note" : "মন্তব্য"}</th>
                </tr>
              </thead>
              <tbody>
                {LAND_UNITS.map((u) => (
                  <tr key={u.id} className="border-t border-border align-top">
                    <td className="p-2 font-medium">{u.en}</td>
                    <td className="p-2 font-bn text-muted-foreground">{u.bn}</td>
                    <td className="p-2 text-right tabular-nums">{formatLandNumber(u.sqft)}</td>
                    <td className="p-2 text-right tabular-nums">{formatLandNumber(u.sqft / 10.7639)}</td>
                    <td className="p-2 text-right tabular-nums">{formatLandNumber(u.sqft / 435.6)}</td>
                    <td className={cn("p-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                      {u.note ? u.note[lang] : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "en" ? "Katha — the standard 720 sq ft" : "কাঠা — প্রচলিত ৭২০ বর্গফুট"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "RAJUK and most BD district land offices record 1 Katha as 720 sq ft (≈ 66.89 sq m, ≈ 1.6529 Decimal). 16 Chatak make 1 Katha; 20 Katha make 1 Bigha. Some older mouzas in North Bengal historically used a smaller katha; always cross-check with the local Khatian."
              : "RAJUK ও বাংলাদেশের বেশিরভাগ ভূমি অফিস ১ কাঠাকে ৭২০ বর্গফুট হিসেবে ধরে (≈ ৬৬.৮৯ বর্গমিটার, ≈ ১.৬৫২৯ ডেসিমেল)। ১৬ ছটাক = ১ কাঠা; ২০ কাঠা = ১ বিঘা। উত্তরবঙ্গের কিছু পুরোনো মৌজায় ছোট কাঠা ব্যবহৃত হত — খতিয়ান যাচাই করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Bigha — 20 Katha = 14,400 sq ft" : "বিঘা — ২০ কাঠা = ১৪,৪০০ বর্গফুট"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Standard Bangladeshi bigha = 14,400 sq ft = 33.058 Decimal = 0.331 Acre. Sylhet, Kishoreganj and some North-Bengal mouzas have historically used a smaller ‘bigha’ (~12,470 sq ft). The Indian (West Bengal) bigha is also 1,600 sq yd = 14,400 sq ft and matches the BD standard."
              : "প্রচলিত বাংলাদেশী বিঘা = ১৪,৪০০ বর্গফুট = ৩৩.০৫৮ ডেসিমেল = ০.৩৩১ একর। সিলেট, কিশোরগঞ্জ ও উত্তরবঙ্গের কিছু মৌজায় ঐতিহাসিকভাবে ছোট ‘বিঘা’ (~১২,৪৭০ বর্গফুট) ব্যবহৃত হয়। ভারতের পশ্চিমবঙ্গের বিঘাও ১,৬০০ বর্গগজ = ১৪,৪০০ বর্গফুট, যা বাংলাদেশের মানের সাথে মিলে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Shotok / Decimal" : "শতক / ডেসিমেল"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Across Bangladesh, ‘Decimal’ and ‘Shotak / Shotangsho’ refer to the same unit: 1/100 of an acre = 435.6 sq ft. Most Khatians in BD list area in Decimal/Shotak — easy to add and split."
              : "বাংলাদেশে ‘ডেসিমেল’ ও ‘শতক / শতাংশ’ একই একক: ১/১০০ একর = ৪৩৫.৬ বর্গফুট। অধিকাংশ খতিয়ানে জমির পরিমাণ ডেসিমেল/শতকে লেখা থাকে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Chittagong / Noakhali / Sylhet — Kani, Gonda, Kora, Kranti, Til" : "চট্টগ্রাম / নোয়াখালী / সিলেট — কানি, গণ্ডা, কড়া, ক্রান্তি, তিল"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Greater Chittagong and Noakhali use the Kani-system. 1 Kani = 40 Decimal = 17,424 sq ft. Sub-units: 20 Gonda = 1 Kani; 4 Kora = 1 Gonda; 4 Kranti = 1 Kora; 4 Til = 1 Kranti. Older Sylhet deeds may also use Kani / Lecha."
              : "বৃহত্তর চট্টগ্রাম ও নোয়াখালীতে কানি-পদ্ধতি প্রচলিত। ১ কানি = ৪০ ডেসিমেল = ১৭,৪২৪ বর্গফুট। উপ-একক: ২০ গণ্ডা = ১ কানি; ৪ কড়া = ১ গণ্ডা; ৪ ক্রান্তি = ১ কড়া; ৪ তিল = ১ ক্রান্তি। সিলেটের পুরোনো দলিলেও কানি/লেছা থাকতে পারে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Acre & Hectare — international standards" : "একর ও হেক্টর — আন্তর্জাতিক মান"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 Acre = 43,560 sq ft = 4,046.86 sq m = 100 Decimal ≈ 3.025 Bigha. 1 Hectare = 10,000 sq m = 2.471 Acre ≈ 7.474 Bigha. Government revenue records often quote large areas in Acres / Hectares."
              : "১ একর = ৪৩,৫৬০ বর্গফুট = ৪,০৪৬.৮৬ বর্গমিটার = ১০০ ডেসিমেল ≈ ৩.০২৫ বিঘা। ১ হেক্টর = ১০,০০০ বর্গমিটার = ২.৪৭১ একর ≈ ৭.৪৭৪ বিঘা।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Reading a Khatian / Porcha" : "খতিয়ান / পর্চা পড়া"} lang={lang}>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Mouza name & JL number — locates the land block." : "মৌজা ও জে.এল. নম্বর — জমি ব্লক চিহ্নিত করে।"}</li>
            <li>{lang === "en" ? "Khatian number + Dag number — identifies the specific plot." : "খতিয়ান নম্বর + দাগ নম্বর — নির্দিষ্ট প্লট চিহ্নিত।"}</li>
            <li>{lang === "en" ? "Owner share (অংশ) — fraction of the dag belonging to each owner." : "অংশ — প্রতি মালিকের ভগ্নাংশ মালিকানা।"}</li>
            <li>{lang === "en" ? "Area in Decimal — total of the dag." : "ডেসিমেলে জমির পরিমাণ — দাগের মোট।"}</li>
            <li>{lang === "en" ? "Class (নাল/ভিটা/পুকুর) — affects price and tax." : "শ্রেণি (নাল/ভিটা/পুকুর) — দাম ও করের ওপর প্রভাব।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Land registration — typical cost split" : "জমি রেজিস্ট্রেশন — সাধারণ খরচের ভাগ"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "For most BD urban land transfers: gain tax 4%, stamp duty 1.5%, VAT 3%, registration fee 1%, local government tax 3% (city corporations) — total ≈ 12.5% of deed value, plus mutation (≈ ৳1,150) and survey costs. Use the registration cost panel above for a rough estimate."
              : "শহুরে জমি হস্তান্তরে সাধারণত: গেইন ট্যাক্স ৪%, স্ট্যাম্প ডিউটি ১.৫%, ভ্যাট ৩%, রেজিস্ট্রেশন ফি ১%, সিটি কর্পোরেশনে স্থানীয় সরকার কর ৩% — মোট ≈ ১২.৫%, সঙ্গে নামজারি (≈ ৳১,১৫০) ও জরিপ খরচ। উপরের প্যানেল ব্যবহার করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Quick reference — popular conversions" : "দ্রুত রেফারেন্স — প্রচলিত রূপান্তর"} lang={lang}>
          <div className="grid gap-2 md:grid-cols-2">
            {[
              { a: "1 Katha", b: "720 sq ft = 1.6529 Decimal" },
              { a: "1 Bigha", b: "20 Katha = 14,400 sq ft = 33.058 Decimal" },
              { a: "1 Acre", b: "100 Decimal = 3.025 Bigha = 60.5 Katha" },
              { a: "1 Hectare", b: "2.471 Acre ≈ 7.474 Bigha = 247.1 Decimal" },
              { a: "1 Decimal", b: "435.6 sq ft = 0.605 Katha" },
              { a: "1 Kani (Ctg)", b: "40 Decimal = 17,424 sq ft = 24.2 Katha" },
              { a: "1 Gonda", b: "2 Decimal = 871.2 sq ft" },
              { a: "1 Sq m", b: "10.7639 sq ft = 0.0247 Decimal" },
            ].map((r) => (
              <div key={r.a} className="rounded-md border border-border bg-card p-2 text-sm">
                <span className="font-semibold">{r.a}</span>
                <span className="text-muted-foreground"> = {r.b}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Tax" : "কর"}
            </Link>
            <Link to="/vat-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              VAT
            </Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Percentage" : "শতকরা"}
            </Link>
            <Link to="/loan-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Loan" : "লোন"}
            </Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function UnitSelect({
  value, onChange, lang, compact,
}: { value: string; onChange: (v: string) => void; lang: Lang; compact?: boolean }) {
  const groups: { cat: LandUnit["category"]; label: string }[] = [
    { cat: "standard",    label: T.category.standard[lang] },
    { cat: "regional",    label: T.category.regional[lang] },
    { cat: "traditional", label: T.category.traditional[lang] },
  ];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full rounded-md border border-border bg-card text-sm focus:border-accent focus:outline-none",
        compact ? "px-2 py-1.5" : "px-3 py-2",
      )}
    >
      {groups.map((g) => (
        <optgroup key={g.cat} label={g.label}>
          {LAND_UNITS.filter((u) => u.category === g.cat).map((u) => (
            <option key={u.id} value={u.id}>
              {u.en} — {u.bn}{u.symbol ? ` (${u.symbol})` : ""}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function DimField({
  label, v, on, useBn,
}: { label: string; v: string; on: (v: string) => void; useBn: boolean }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs text-muted-foreground">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={useBn ? toBengaliNumerals(v) : v}
        onChange={(e) => on(e.target.value.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString()))}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm tabular-nums focus:border-accent focus:outline-none"
      />
    </div>
  );
}

function Section({
  title, children, lang,
}: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section className="space-y-3">
      <h2 className={cn("text-lg font-semibold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

// Suppress unused-import warnings for icons reserved for future "Save"/"Open" UI.
void Save; void FolderOpen;
